"use client";

import { useId, type ReactNode } from "react";

import { cn } from "@/lib/utils";

/** Minimum height shared by every hero offer panel state so the page below does not jump. */
export const DEAL_OFFER_PANEL_MIN_HEIGHT = "min-h-64";

function panelBodyClass(compact: boolean) {
  return compact ? "px-4 py-3 sm:px-5" : "p-4 sm:px-6 sm:py-5";
}

/** Label (or control) left, freshness right; renders nothing when all three are empty. */
function DealOfferPanelHeadingRow({
  control,
  heading,
  headingId,
  meta,
  tone,
}: {
  control?: ReactNode;
  heading?: string;
  headingId: string;
  meta?: ReactNode;
  tone: "primary" | "muted";
}) {
  if (!heading && !control && !meta) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
      {control && <div className="min-w-0 max-w-full">{control}</div>}
      {heading && (
        <h2
          id={headingId}
          className={cn(
            "min-w-0 text-xs font-bold uppercase tracking-wider",
            tone === "primary" ? "text-primary" : "text-muted-foreground",
          )}
        >
          {heading}
        </h2>
      )}
      {meta}
    </div>
  );
}

/**
 * Shell of the hero offer panel: one rounded card with an uppercase heading row
 * (label or control left, freshness right), a body, and optional footer strips.
 */
export function DealOfferPanel({
  heading,
  headingControl,
  headingTone = "primary",
  headingMeta,
  children,
  footer,
  busy,
  compact = false,
  className,
}: {
  heading?: string;
  /**
   * Replaces the visible heading label (work scope: the offer-format switch). `heading` then only
   * names the section for assistive technology. Pass `null` to hide the label without a control.
   */
  headingControl?: ReactNode;
  headingTone?: "primary" | "muted";
  headingMeta?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  busy?: boolean;
  /** Short states (no offers, error): no reserved height, tighter padding. */
  compact?: boolean;
  className?: string;
}) {
  const headingId = useId();
  const showHeadingLabel = Boolean(heading) && headingControl === undefined;

  return (
    <section
      aria-labelledby={showHeadingLabel ? headingId : undefined}
      aria-label={showHeadingLabel ? undefined : heading}
      aria-busy={busy || undefined}
      className={cn(
        "flex flex-col overflow-hidden rounded-2xl border border-border bg-card",
        !compact && DEAL_OFFER_PANEL_MIN_HEIGHT,
        className,
      )}
    >
      <div className={cn("flex flex-1 flex-col gap-3.5", panelBodyClass(compact))}>
        <DealOfferPanelHeadingRow
          control={headingControl}
          heading={showHeadingLabel ? heading : undefined}
          headingId={headingId}
          meta={headingMeta}
          tone={headingTone}
        />
        {children}
      </div>
      {footer}
    </section>
  );
}
